import { Trans, t } from '@lingui/macro';
import { Languages } from 'lucide-react';
import { useLocale, SUPPORTED_LOCALES, type SupportedLocale, localizePath } from './index';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { useCallback } from 'react';
import { cn } from '@shared/lib/utils';
import { useLocation, useNavigate } from 'react-router-dom';

const LOCALE_LABEL: Record<SupportedLocale, string> = {
  en: 'English',
  es: 'Español',
};

export const LanguageSwitcher = () => {
  const { locale, setLocale } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLocaleChange = useCallback(
    (newLocale: SupportedLocale) => {
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      const nextPath = localizePath(currentPath, newLocale);
      // Prevent setting the same locale again
      if (newLocale === locale) {
        return;
      }

      setLocale(newLocale);
      navigate(nextPath, { replace: true });
    },
    [locale, location.hash, location.pathname, location.search, navigate, setLocale]
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'inline-flex size-10 items-center justify-center rounded-md',
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/30',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
        )}
        aria-label={t({
          id: 'shared.i18n.language-switcher.switch-language',
          message: 'Switch language',
        })}
      >
        <Languages className='h-4 w-4' />
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='min-w-[160px]'>
        <DropdownMenuLabel>
          <Trans id='shared.i18n.language-switcher.language'>Language</Trans>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LOCALES.map(code => (
          <DropdownMenuItem
            key={code}
            onSelect={() => {
              handleLocaleChange(code);
            }}
            className={locale === code ? 'font-semibold' : undefined}
          >
            {LOCALE_LABEL[code]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
